import { Link, useLocation } from 'react-router';
import Badge from '../../components/redesign/Badge';
import type { TabEntry } from './navigation';

interface MobileTabBarProps {
    /**
     * Computed once by the caller (`tabsFor(flags)` in `RedesignLayout`) and passed down, rather
     * than this component re-deriving it from the flags itself — the promotion rule lives in
     * exactly one place, and the More sheet's item list is derived from that same array.
     */
    tabs: TabEntry[];
    /** Whether the More sheet is currently open — drives the button's aria-expanded. */
    moreOpen: boolean;
    onToggleMore: () => void;
    /** Count shown on the Enquiries tab's badge. Omitted/zero renders no badge, not a "0". */
    enquiriesCount?: number;
    /** Count shown on the Saved tab's badge, when Saved is a tab. Same zero rule. */
    savedCount?: number;
    /**
     * Count shown on the More button: what is new behind it — the new-saves count when Saved sits
     * in the More sheet (marketplace on) rather than the bar, so it is not invisible on a phone.
     * Same zero rule.
     */
    moreCount?: number;
}

/**
 * The prototype's five fixed tab icons, straight out of `app-prototype.html`'s `svgIcon()`/`TABS`
 * (house, magnifier, speech bubble, two bars, three dots).
 *
 * `saved` and `alerts` render here too, once the marketplace flags are off and `tabsFor` promotes
 * them into the bar — which is production, not an edge case. The prototype's own icon set has no
 * icon for either (it assumes the marketplace is always live, so its tab bar never runs without
 * Enquiries/Deals in those slots), so these two are newly authored to fill that gap: a five-point
 * star outline and an envelope outline, drawn in the same flat `stroke="currentColor"` style as
 * the other three rather than reusing the sidebar's `☆`/`✉` text glyphs. Text glyphs looked like
 * the lower-effort choice, but `✉` (U+2709) renders as a colour emoji on several platforms, which
 * would sit badly next to monochrome SVGs in the same bar — and this is what real users see
 * whenever the marketplace is dark.
 */
function TabIcon({ tabKey }: { tabKey: string }) {
    switch (tabKey) {
        case 'dashboard':
            return (
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <path d="m3 9 9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
                </svg>
            );
        case 'map':
            return (
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <circle cx="11" cy="11" r="7" />
                    <path d="m21 21-4.35-4.35" />
                </svg>
            );
        case 'enquiries':
            return (
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
                </svg>
            );
        case 'deals':
            return (
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <rect x="3" y="3" width="7" height="18" rx="1.5" />
                    <rect x="14" y="3" width="7" height="10" rx="1.5" />
                </svg>
            );
        case 'more':
            return (
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <circle cx="5" cy="12" r="1.6" />
                    <circle cx="12" cy="12" r="1.6" />
                    <circle cx="19" cy="12" r="1.6" />
                </svg>
            );
        case 'saved':
            return (
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <path d="M12 2l3.09 6.26L22 9.27l-5 4.87L18.18 21 12 17.77 5.82 21 7 14.14 2 9.27l6.91-1.01L12 2z" />
                </svg>
            );
        case 'alerts':
            return (
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                    <rect x="2" y="4" width="20" height="16" rx="2" />
                    <path d="m22 7-10 6L2 7" />
                </svg>
            );
        default:
            return null;
    }
}

/**
 * The prototype's fixed bottom tab bar. Hidden at `md:` and up, where the sidebar takes over.
 */
export default function MobileTabBar({ tabs, moreOpen, onToggleMore, enquiriesCount = 0, savedCount = 0, moreCount = 0 }: MobileTabBarProps) {
    const { pathname } = useLocation();

    // `data-mobile-tab-bar` is RedesignLayout's handle on this element: the account-banner strip
    // is pinned immediately above the bar, and the bar's height is the sum of its own padding,
    // icon, label line-box, border and safe-area inset — a number this component owns and nothing
    // else should restate. The layout measures it off this attribute rather than guessing.
    return (
        <nav data-mobile-tab-bar className="md:hidden fixed left-0 right-0 bottom-0 z-90 flex bg-surface border-t border-border pb-[env(safe-area-inset-bottom,0px)]">
            {tabs.map((tab) => {
                const badgeCount = tab.badge === 'enquiries' ? enquiriesCount
                    : tab.badge === 'saved' ? savedCount
                    : tab.key === 'more' ? moreCount
                    : 0;
                const icon = (
                    <span className="grid place-items-center leading-[0] relative">
                        <TabIcon tabKey={tab.key} />
                        {badgeCount > 0 && (
                            <span className="absolute -top-1.5 left-[13px]">
                                <Badge variant="tab-count">{badgeCount}</Badge>
                            </span>
                        )}
                    </span>
                );

                if (tab.key === 'more') {
                    return (
                        <button
                            key={tab.key}
                            type="button"
                            aria-expanded={moreOpen}
                            aria-controls="mobile-more-sheet"
                            aria-label={moreCount > 0 ? `More, ${moreCount} new` : undefined}
                            onClick={onToggleMore}
                            className="flex-1 flex flex-col items-center gap-1 font-semibold text-[11.5px] leading-none text-ink-faint pt-[9px] px-0.5 pb-[10px]"
                        >
                            {icon}
                            <span className="max-w-full truncate whitespace-nowrap">{tab.label}</span>
                        </button>
                    );
                }

                const active = pathname === tab.to;
                return (
                    <Link
                        key={tab.key}
                        to={tab.to}
                        aria-current={active ? 'page' : undefined}
                        aria-label={tab.fullLabel}
                        className={`flex-1 flex flex-col items-center gap-1 font-semibold text-[11.5px] leading-none pt-[9px] px-0.5 pb-[10px] ${
                            // `text-heading`, not `text-sidebar`: sidebar is a BACKGROUND token that
                            // darkens to #0c2a17 in dark mode, which made the current tab vanish
                            // against the bar. Heading is the same deep green in light, pale in dark.
                            active ? 'text-heading font-bold' : 'text-ink-faint'
                        }`}
                    >
                        {icon}
                        {/* One line, always: a wrapped label drops its tab below the others. */}
                        <span className="max-w-full truncate whitespace-nowrap">{tab.label}</span>
                    </Link>
                );
            })}
        </nav>
    );
}
